/**
 * TeamMemberRow Component
 *
 * Displays a single team member with avatar, name, segmented workload bar,
 * and count badges for done/active/overdue tasks.
 *
 * @module pages/DashboardPage/components/TeamMemberRow
 */

import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';
import clsx from 'clsx';

import { UserAvatar } from '@/components/user/UserAvatar';

import type { TeamMemberRowProps } from './TeamMemberRow.types';
import styles from './TeamMemberRow.module.css';

/** Percentage multiplier for width calculations */
const PERCENTAGE_MULTIPLIER = 100;

/**
 * TeamMemberRow renders a row for a single team member
 * with avatar, first name, segmented bar, and count badges.
 */
export const TeamMemberRow = memo(function TeamMemberRow({
  member,
  maxTasks,
}: TeamMemberRowProps) {
  const total = member.done + member.active + member.overdue;
  const scale = maxTasks > 0 ? total / maxTasks : 0;

  const barStyle = useMemo(
    () =>
      ({
        '--bar-scale': `${scale * PERCENTAGE_MULTIPLIER}%`,
        '--done-width':
          total > 0
            ? `${(member.done / total) * PERCENTAGE_MULTIPLIER}%`
            : '0%',
        '--active-width':
          total > 0
            ? `${(member.active / total) * PERCENTAGE_MULTIPLIER}%`
            : '0%',
        '--overdue-width':
          total > 0
            ? `${(member.overdue / total) * PERCENTAGE_MULTIPLIER}%`
            : '0%',
      }) as CSSProperties,
    [scale, member.done, member.active, member.overdue, total]
  );

  return (
    <div className={styles.row}>
      <UserAvatar
        displayName={member.name}
        avatarUrl={member.avatarUrl}
        size="sm"
      />
      <span className={styles.name}>{member.name}</span>
      <div className={styles.barContainer} style={barStyle}>
        <div className={styles.bar}>
          <div className={clsx(styles.segment, styles.segmentDone)} />
          <div className={clsx(styles.segment, styles.segmentActive)} />
          <div className={clsx(styles.segment, styles.segmentOverdue)} />
        </div>
      </div>
      <div className={styles.badges}>
        <span className={clsx(styles.badge, styles.green)}>{member.done}</span>
        <span className={clsx(styles.badge, styles.blue)}>{member.active}</span>
        <span
          className={clsx(
            styles.badge,
            member.overdue > 0 ? styles.red : styles.gray
          )}
        >
          {member.overdue}
        </span>
      </div>
    </div>
  );
});

TeamMemberRow.displayName = 'TeamMemberRow';
